(() => {
  const paginationContainer = document.querySelector('.js-pagination');

  const getElementById = (id) => document.getElementById(id);
  const querySelector = (selector) => document.querySelector(selector);
  let showMoreButtonClicked = false;

  const getCollectionData = () => {
    const collectionScript = getElementById('collection');
    return {
      collectionJson: JSON.parse(collectionScript.innerHTML),
      nostoId: collectionScript.dataset.nostoId,
    };
  };

  const getFallBackImage = () => {
    const fallbackImage = getElementById('fallback_image');
    return fallbackImage.dataset.fallbackUrl;
  };

  const buildFilters = (facets) =>
    Object.entries(facets).map(([field, value]) => ({ field, value }));

  const buildFiltersString = (filters) =>
    filters
      .map((filter) => `{ field: "${filter.field}", value: ["${filter.value.join('","')}"] }`)
      .join(', ');

  const buildSortString = (sort) =>
    sort
      .map((sortOption) => `{ field: "${sortOption.field}", order: ${sortOption.order} }`)
      .join(', ');

  const buildQuery = ({ categoryId, filters, sort, page, nostoId }) => `
    query {
      search(
        accountId: "${nostoId}"
        products: {
          categoryId: "${categoryId}"
          size: 48
          from: ${page * 48}
          ${sort.length ? `sort: [${buildSortString(sort)}]` : ''}
          filter: [${buildFiltersString(filters)}]
        }
      ) {
        products {
          hits {
            productId
            name
            url
            imageUrl
            alternateImageUrls
            price
            listPrice
            tags1
            brand
            ratingValue
            reviewCount
            skus {
              id
              url
              imageUrl
              name
              availability
              customFields {
                key
                value
              }
              price
              listPrice
            }
          }
          facets {
            ... on SearchStatsFacet {
              id
              field
              type
              name
              min
              max
            }
            ... on SearchTermsFacet {
              id
              field
              type
              name
              data {
                value
                count
                selected
              }
            }
          }
          total
          fuzzy
          size
          from
          categoryId
        }
      }
    }`;

  const fetchProducts = async (categoryId, facets, sort, page, nostoId) => {
    const query = buildQuery({
      categoryId,
      filters: buildFilters(facets),
      sort,
      page,
      nostoId,
    });

    const response = await fetch('https://search.nosto.com/v1/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }

    const result = await response.json();

    // eslint-disable-next-line no-undef
    nostojs((api) => {
      api.recordSearch(
        'category',
        {
          products: {
            categoryId,
            size: 48,
            from: page * 48,
            sort: sort.length ? [buildSortString(sort)] : null,
            filter: [buildFiltersString(buildFilters(facets))],
          },
        },
        {
          products: result.data.search.products,
        },
        {
          isKeyword: false,
        },
      );
    });

    return result.data.search.products;
  };

  const formatToGermanCurrency = (price) =>
    new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);

  const generateImageMarkup = (imageUrl, name, width = 330) => {
    const updatedImageUel = !imageUrl ? getFallBackImage() : imageUrl;

    return `<img
    class="contain"
    src="${updatedImageUel}&amp;width=${width}"
    alt="${name}"
    width="${width}"
    height="${width}"
  />`;
  };

  const generateImageMarkupWithHover = (imageURLMain, imageURLSecond, name) => {
    if (imageURLSecond) {
      const secondUrl = imageURLSecond[0];
      return `
            <img
            class="contain card-product-image-first"
            src="${imageURLMain}&amp;width=330"
            alt="${name}"
            width="330"
            height="330"
          />
          <img
            class="contain"
            src="${secondUrl}&amp;width=330"
            alt="${name}"
            width="330"
            height="330"
          />`;
    }
    return `
            <img
            class="contain"
            src="${imageURLMain}&amp;width=330"
            alt="${name}"
            width="330"
            height="330"
          />`;
  };

  const findAllColorVariants = (skus) => {
    const colorTranslations = new Set(['farbe', 'couleur', 'color']);
    return skus.reduce((acc, sku) => {
      const colorField = sku.customFields.find((field) => colorTranslations.has(field.key));
      if (colorField && (!acc.has(colorField.value) || !acc.get(colorField.value).inStock)) {
        acc.set(colorField.value, {
          image: sku.imageUrl,
          id: sku.id,
          inStock: sku.availability !== 'OutOfStock',
        });
      }
      return acc;
    }, new Map());
  };

  const generateSwatchMarkup = (skus, product) => {
    const variants = Array.from(findAllColorVariants(skus).entries());

    const thumbnails = variants.slice(0, 4).map(([color, { image, id, inStock }], index) => {
      const hiddenMobileClass = index > 1 ? 'card_product__thumbnail--hidden-mobile' : '';
      let overlayMarkup = '';

      if (!inStock) {
        overlayMarkup = `<div class="card-product__thumbnail--overlay-unavailable">
          <svg xmlns="http://www.w3.org/2000/svg" width="33" height="23" viewBox="0 0 33 23" fill="none">
          <path d="M0.772054 1.34376L0.75 1.39273V1.44643V21.0757V21.0764V21.1281L0.77056 21.1757C0.870449 21.4065 1.01941 21.5675 1.21588 21.6641C1.40333 21.7562 1.61223 21.7786 1.81412 21.7786H1.81432C10.2431 21.7717 18.672 21.7719 27.1009 21.772C28.398 21.7721 29.695 21.7721 30.992 21.7721C31.3122 21.7721 31.6166 21.7426 31.8163 21.5382C32.014 21.336 32.0428 21.0294 32.0428 20.7018V20.665V20.6281V20.5912V20.5544V20.5175V20.4807V20.4438V20.4069V20.3701V20.3332V20.2964V20.2595V20.2226V20.1858V20.1489V20.1121V20.0752V20.0383V20.0015V19.9646V19.9277V19.8909V19.854V19.8172V19.7803V19.7434V19.7066V19.6697V19.6329V19.596V19.5591V19.5223V19.4854V19.4486V19.4117V19.3748V19.338V19.3011V19.2642V19.2274V19.1905V19.1537V19.1168V19.0799V19.0431V19.0062V18.9694V18.9325V18.8956V18.8588V18.8219V18.785V18.7482V18.7113V18.6745V18.6376V18.6007V18.5639V18.527V18.4902V18.4533V18.4164V18.3796V18.3427V18.3059V18.269V18.2321V18.1953V18.1584V18.1215V18.0847V18.0478V18.011V17.9741V17.9372V17.9004V17.8635V17.8267V17.7898V17.7529V17.7161V17.6792V17.6424V17.6055V17.5686V17.5318V17.4949V17.458V17.4212V17.3843V17.3475V17.3106V17.2737V17.2369V17.2V17.1632V17.1263V17.0894V17.0526V17.0157V16.9788V16.942V16.9051V16.8683V16.8314V16.7945V16.7577V16.7208V16.684V16.6471V16.6102V16.5734V16.5365V16.4997V16.4628V16.4259V16.3891V16.3522V16.3153V16.2785V16.2416V16.2048V16.1679V16.131V16.0942V16.0573V16.0205V15.9836V15.9467V15.9099V15.873V15.8361V15.7993V15.7624V15.7256V15.6887V15.6518V15.615V15.5781V15.5413V15.5044V15.4675V15.4307V15.3938V15.357V15.3201V15.2832V15.2464V15.2095V15.1726V15.1358V15.0989V15.0621V15.0252V14.9883V14.9515V14.9146V14.8778V14.8409V14.804V14.7672V14.7303V14.6935V14.6566V14.6197V14.5829V14.546V14.5091V14.4723V14.4354V14.3986V14.3617V14.3248V14.288V14.2511V14.2143V14.1774V14.1405V14.1037V14.0668V14.0299V13.9931V13.9562V13.9194V13.8825V13.8456V13.8088V13.7719V13.7351V13.6982V13.6613V13.6245V13.5876V13.5508V13.5139V13.477V13.4402V13.4033V13.3664V13.3296V13.2927V13.2559V13.219V13.1821V13.1453V13.1084V13.0716V13.0347V12.9978V12.961V12.9241V12.8872V12.8504V12.8135V12.7767V12.7398V12.7029V12.6661V12.6292V12.5924V12.5555V12.5186V12.4818V12.4449V12.4081V12.3712V12.3343V12.2975V12.2606V12.2237V12.1869V12.15V12.1132V12.0763V12.0394V12.0026V11.9657V11.9289V11.892V11.8551V11.8183V11.7814V11.7446V11.7077V11.6708V11.634V11.5971V11.5602V11.5234V11.4865V11.4497V11.4128V11.3759V11.3391V11.3022V11.2654V11.2285V11.1916V11.1548V11.1179V11.081V11.0442V11.0073V10.9705V10.9336V10.8967V10.8599V10.823V10.7862V10.7493V10.7124V10.6756V10.6387V10.6019V10.565V10.5281V10.4913V10.4544V10.4175V10.3807V10.3438V10.307V10.2701V10.2332V10.1964V10.1595V10.1227V10.0858V10.0489V10.0121V9.97521V9.93835V9.90149V9.86463V9.82777V9.79091V9.75404V9.71718V9.68032V9.64346V9.6066V9.56974V9.53288V9.49602V9.45915V9.42229V9.38543V9.34857V9.31171V9.27485V9.23799V9.20113V9.16426V9.1274V9.09054V9.05368V9.01682V8.97996V8.9431V8.90624V8.86937V8.83251V8.79565V8.75879V8.72193V8.68507V8.64821V8.61135V8.57448V8.53762V8.50076V8.4639V8.42704V8.39018V8.35332V8.31646V8.27959V8.24273V8.20587V8.16901V8.13215V8.09529V8.05843V8.02157V7.9847V7.94784V7.91098V7.87412V7.83726V7.8004V7.76354V7.72668V7.68982V7.65295V7.61609V7.57923V7.54237V7.50551V7.46865V7.43179V7.39493V7.35806V7.3212V7.28434V7.24748V7.21062V7.17376V7.1369V7.10004V7.06317V7.02631V6.98945V6.95259V6.91573V6.87887V6.84201V6.80515V6.76828V6.73142V6.69456V6.6577V6.62084V6.58398V6.54712V6.51026V6.47339V6.43653V6.39967V6.36281V6.32595V6.28909V6.25223V6.21537V6.1785V6.14164V6.10478V6.06792V6.03106V5.9942V5.95734V5.92048V5.88361V5.84675V5.80989V5.77303V5.73617V5.69931V5.66245V5.62559V5.58872V5.55186V5.515V5.47814V5.44128V5.40442V5.36756V5.3307V5.29383V5.25697V5.22011V5.18325V5.14639V5.10953V5.07267V5.03581V4.99894V4.96208V4.92522V4.88836V4.8515V4.81464V4.77778V4.74092V4.70405V4.66719V4.63033V4.59347V4.55661V4.51975V4.48289V4.44603V4.40916V4.3723V4.33544V4.29858V4.26172V4.22486V4.188V4.15114V4.11427V4.07741V4.04055V4.00369V3.96683V3.92997V3.89311V3.85625V3.81938V3.78252V3.74566V3.7088V3.67194V3.63508V3.59822V3.56136V3.52449V3.48763V3.45077V3.41391V3.37705V3.34019V3.30333V3.26647V3.2296V3.19274V3.15588V3.11902V3.08216V3.0453V3.00844V2.97158V2.93471V2.89785V2.86099V2.82413V2.78727V2.75041V2.71355V2.67669V2.63982V2.60296V2.5661V2.52924V2.49238V2.45552V2.41866V2.3818V2.34493V2.30807V2.27121V2.23435V2.19749V2.16063V2.12377V2.08691V2.05004V2.01318V1.97632V1.93946V1.9026V1.86574V1.82888C32.0428 1.65447 32.0338 1.49764 32.0043 1.36266C31.9743 1.22521 31.9199 1.09543 31.8183 0.990943C31.6191 0.785898 31.3146 0.757189 30.9955 0.757189C28.9479 0.757038 26.9003 0.757014 24.8528 0.75699C17.1744 0.7569 9.49612 0.756811 1.81784 0.75H1.81762C1.61546 0.75 1.40736 0.772211 1.22019 0.862905C1.02423 0.957859 0.874529 1.11624 0.772054 1.34376ZM20.6733 12.5488L20.6738 12.5483L20.6747 12.5475C20.7359 12.4899 20.7971 12.4322 20.863 12.3703C23.6324 14.9566 26.3945 17.536 29.2033 20.1585H3.60347C6.39431 17.5246 9.13589 14.9372 11.9039 12.3251C11.9682 12.3817 12.0297 12.4364 12.0909 12.4935C12.5403 12.9144 12.9895 13.3356 13.4387 13.7569C14.1921 14.4634 14.9457 15.17 15.7006 15.8753L15.7007 15.8754C15.9178 16.078 16.1404 16.2575 16.4059 16.2592C16.6728 16.2609 16.8959 16.0828 17.1129 15.8806L17.113 15.8806C17.9707 15.0814 18.8263 14.2795 19.6815 13.478C20.0121 13.1682 20.3427 12.8584 20.6733 12.5488ZM16.4108 14.3466C12.4066 10.5981 8.4101 6.85682 4.36141 3.06676L3.60792 2.3614H29.213L28.4879 3.04012C24.4345 6.83481 20.4276 10.5858 16.4108 14.3466ZM23.3401 12.492L22.0357 11.2741L30.4436 3.40249V19.1249C28.0645 16.9032 25.7067 14.7018 23.3401 12.492ZM2.39901 19.0935V3.42401C5.18788 6.03466 7.95272 8.62234 10.7354 11.2266C9.55808 12.3376 8.3837 13.4458 7.20831 14.5551C5.61041 16.063 4.01064 17.5727 2.39901 19.0935Z" fill="black" stroke="black" stroke-width="0.5"/>
          </svg>
        </div>`;
      }

      return `
        <a href="${
          product.url
        }?variant=${id}" class="card_product__thumbnail ${hiddenMobileClass}" aria-label="${
        product.name
      } - ${color}">
          <div class="card_product__thumbnail-image">
            ${generateImageMarkup(image, color, 64)}
          </div>
          ${overlayMarkup}
        </a>`;
    });

    const numberOfColors = variants.length;
    if (numberOfColors > 2) {
      thumbnails.push(
        `<div class="card_product__thumbnail-more card_product__thumbnail-more--mobile">
          +${numberOfColors - 2}
        </div>`,
      );
    }
    if (numberOfColors > 4) {
      thumbnails.push(
        `<div class="card_product__thumbnail-more card_product__thumbnail-more--desktop">
          +${numberOfColors - 4}
        </div>`,
      );
    }

    return thumbnails.join('');
  };

  const getPrices = (skus) => {
    if (!skus || skus.length === 0) return null;

    // Find the variant with the lowest price
    const lowestPriceSku = skus.reduce(
      (lowest, sku) => (sku.price < lowest.price ? sku : lowest),
      skus[0],
    );

    const showListPrice = lowestPriceSku.listPrice >= lowestPriceSku.price * 1.05;

    const hasDifferentPrices = skus.some((sku) => sku.price !== skus[0].price);

    return {
      lowestPrice: lowestPriceSku.price,
      listPrice: lowestPriceSku.listPrice,
      showListPrice,
      hasDifferentPrices,
    };
  };

  const getRatingStars = (ratingValue, reviewCount) => {
    if (reviewCount && ratingValue) {
      const ratingInt = Math.round(ratingValue);
      const ratingRest = 5 - ratingInt;

      let stars = '';

      // Generate full stars
      for (let i = 0; i < ratingInt; i += 1) {
        stars += '<span class="nosto-star-full">★</span>';
      }

      // Generate empty stars
      for (let i = 0; i < ratingRest; i += 1) {
        stars += '<span class="nosto-star-empty">★</span>';
      }

      // Return the full HTML string
      return `
        <div class="nosto-rating">
          <div class="nosto-stars">
            ${stars}
          </div>
          <span class="nosto-reviews">(${reviewCount})</span>
        </div>
      `;
    }

    return '';
  };

  const generateProductsMarkup = (products) =>
    products
      .map((product) => {
        const { lowestPrice, listPrice, showListPrice, hasDifferentPrices } = getPrices(
          product.skus,
        );
        let productHandle = '';
        const pathParts = product.url.split('/').filter((part) => part.trim() !== '');
        productHandle = pathParts[pathParts.length - 1];
        const isSoldOut = product.skus.every((sku) => sku.availability === 'OutOfStock');
        const isFrench =
        document.documentElement.lang === "fr" ||
        window.location.pathname.includes("/fr/");
      const soldOutMessage = isFrench ? "En rupture de stock" : "Sold Out";

        return `<article data-limoniapps-discountninja-product-handle="${productHandle}" class="card-product">
                <div class="card-product__image">
                    <a
                    href="${product.url}"
                    class="js-product"
                    data-product-id="${product.productId}"
                    data-url="${product.url}"
                    >
                    ${generateImageMarkupWithHover(
                      product.imageUrl,
                      product.alternateImageUrls,
                      product.name,
                    )}
                    </a>
                    ${
                      isSoldOut ? `<div class="card-product__sold">${soldOutMessage}</div>` : ''
                    }          
                </div>

                <div class="card-product__thumbnails">
                    ${generateSwatchMarkup(product.skus, product)}
                </div>
                <a
                    class="card-product__info 2"
                    href="${product.url}"
                >
                    ${getRatingStars(product.ratingValue, product.reviewCount)}

                    <div class="card-product__vendor">${product.brand}</div>

                    <h3 class="card-product__title">${product.name}</h3>
                    <div class="price-wrapper limoniapps-discountninja-productprice">
                      <div class="product-price-block">
                      ${
                        showListPrice
                          ? `<span class="product-price-block__compare-at-price">
                        ${formatToGermanCurrency(listPrice)}
                      </span><span class="product-price-block__price --discounted"
                      >${hasDifferentPrices ? 'À partir de ' : ''}${formatToGermanCurrency(
                              lowestPrice,
                            )}</span>`
                          : `<span class="product-price-block__price"
                      >${hasDifferentPrices ? 'À partir de ' : ''}${formatToGermanCurrency(
                              lowestPrice,
                            )}</span>`
                      }
                      </div>
                    </div>
                </a>
            </article>
            `;
      })
      .join('');

  const generateFacetsMarkup = (facets, selectedFacets = {}) =>
    facets
      .filter((facet) => facet?.data?.length > 1 && facet.field !== 'customFields.producttype')
      .map((facet) => {
        if (facet.field === 'price') {
          return `<div class="facet price-facet">
            <button class="price-facet__button" id="price-filter-button">
              ${facet.name}
              <svg width="15" height="8" viewBox="0 0 15 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.5098 1L11.0099 4L7.50977 7L0.509766 0.999999" stroke="black" stroke-width="1.5"/>
              </svg>
            </button>

            <div class="price-facet__dropdown" id="price-dropdown">
              <div class="price-facet__filter">
                <label for="price-range" class="price-facet__label">${facet.name}</label>

                <div id="js-price-facet__slider"></div>

                <div class="price-facet__inputs">
                  <div class="price-facet__input">
                    <label for="price-min">From</label>
                    <input
                      type="number"
                      id="price-min"
                      class="price-facet__input-field"
                      value="${facet.min}"
                      min="${facet.min}"
                      max="${facet.max}"
                      step="0.01"
                    />
                  </div>
                  <div class="price-facet__input">
                    <label for="price-max">To</label>
                    <input
                      type="number"
                      id="price-max"
                      class="price-facet__input-field"
                      value="${facet.max}"
                      min="${facet.min}"
                      max="${facet.max}"
                      step="0.01"
                    />
                  </div>
                </div>

                <button class="price-facet__apply">Apply</button>
              </div>
            </div>
          </div>
          `;
        }

        // Check if the facet has exactly two entries and the values are true and false
        if (
          facet.data.length === 2 &&
          facet.data.some((option) => option.value === 'true') &&
          facet.data.some((option) => option.value === 'false')
        ) {
          const isSelected = selectedFacets[facet.field]?.includes('true');

          return `<div class="facet toggle-facet">
            ${facet.name}
            <label class="switch">
              <input type="checkbox" ${isSelected ? 'checked' : ''} id="${facet.field}" />
              <span class="switch__slider"></span>
            </label>
          </div>
          `;
        }

        // Default multi-select dropdown for other facets
        return `
              <div class="facet multi-select-facet">
                <div class="multi-select-container">
                  <button class="multi-select-toggle" aria-haspopup="true" aria-expanded="false">
                    ${facet.name}
                    ${
                      selectedFacets[facet.field]?.length
                        ? `<span class='selected--facets__count'>${
                            selectedFacets[facet.field].length
                          }</span>`
                        : ''
                    }
                    <svg width="15" height="8" viewBox="0 0 15 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.5098 1L11.0099 4L7.50977 7L0.509766 0.999999" stroke="black" stroke-width="1.5"/>
                    </svg>
                  </button>
                  <ul class="multi-select-dropdown">
                    ${facet.data
                      .map(
                        (option) => `
                        <li class="multi-select-option">
                          <input type="checkbox" id="${facet.field}||${option.value}" value="${
                          option.value
                        }" ${
                          selectedFacets[facet.field]?.includes(option.value) ? 'checked' : ''
                        } />
                          <label for="${facet.field}-${option.value}">${option.value}</label>
                          <span class='option__count'>${option.count}</span>
                        </li>`,
                      )
                      .join('')}
                  </ul>
                </div>
              </div>`;
      })
      .join('');

  const generatePagination = (total, currentPage) => {
    const totalPages = Math.ceil(total / 48);
    paginationContainer.innerHTML = '';
    window.renderPagination(totalPages, currentPage + 1);
  };

  const generateProductTypeFacetMarkup = (productTypes) => {
    const allProductsSelected = productTypes.every((productType) => !productType.selected);
    const slidesMarkup = productTypes
      .map((productType) => {
        const { value, selected } = productType;
        const activeClass = selected ? 'product-type-facets--active' : '';

        return `<div class="swiper-slide product-type-facets ${activeClass}">${value}</div>`;
      })
      .join('');

    // Mobile slider markup
    const mobileMarkup = `
      <div class="swiper">
        <div class="swiper-wrapper">
          <div class="swiper-slide product-type-facets ${
            allProductsSelected ? 'product-type-facets--active' : ''
          } product-type-facets--all">Alle Produkte</div>
        ${slidesMarkup}
        </div>
      </div>
    `;

    let visibleSlides;
    let hiddenSlides;
    let showMoreButton = '';

    if (showMoreButtonClicked) {
      // Show all slides if the "Show More" button has been clicked
      visibleSlides = productTypes
        .map((productType) => {
          const { value, selected } = productType;
          const activeClass = selected ? 'product-type-facets--active' : '';

          return `<div class="product-type-facets ${activeClass}">${value}</div>`;
        })
        .join('');
      hiddenSlides = ''; // No hidden slides
    } else {
      // Show up to 6 slides initially, and hide the rest
      visibleSlides = productTypes
        .slice(0, 5)
        .map((productType) => {
          const { value, selected } = productType;
          const activeClass = selected ? 'product-type-facets--active' : '';

          return `<div class="product-type-facets ${activeClass}">${value}</div>`;
        })
        .join('');

      hiddenSlides = productTypes
        .slice(5)
        .map((productType) => {
          const { value, selected } = productType;
          const activeClass = selected ? 'product-type-facets--active' : '';

          return `<div class="product-type-facets ${activeClass} hidden">${value}</div>`;
        })
        .join('');

      // Show more button if there are more than 6 slides and the "Show More" button has not been clicked
      if (productTypes.length > 5) {
        showMoreButton = `<button class="product-type-facets__show-more">mehr anzeigen</button>`;
      }
    }

    const desktopMarkup = `
      <div class="product-type-facets-container">
        <div class="product-type-facets product-type-facets--all ${
          allProductsSelected ? 'product-type-facets--active' : ''
        }">Alle Produkte</div>
        ${visibleSlides}
        ${hiddenSlides}
        ${showMoreButton}
      </div>
    `;

    return { mobileMarkup, desktopMarkup };
  };

  const initializeProductTypes = (productTypes) => {
    const productTypesContainer = document.querySelector('.js-product-types');
    const { mobileMarkup, desktopMarkup } = generateProductTypeFacetMarkup(productTypes);

    if (window.innerWidth < 1024) {
      productTypesContainer.innerHTML = mobileMarkup;
      const productTypesSwiper = productTypesContainer.querySelector('.swiper');

      // eslint-disable-next-line no-undef, no-new
      new Swiper(productTypesSwiper, {
        slidesPerView: 'auto',
        spaceBetween: 12,
      });
    } else {
      productTypesContainer.innerHTML = desktopMarkup;
      document.addEventListener('click', (e) => {
        if (e.target.classList.contains('product-type-facets__show-more')) {
          const hiddenSlides = document.querySelectorAll('.product-type-facets.hidden');
          hiddenSlides.forEach((slide) => {
            slide.classList.remove('hidden');
          });
          e.target.style.display = 'none'; // Hide the "Show More" button after clicking
          showMoreButtonClicked = true;
        }
      });
    }
  };

  const populateProductCount = (totalProducts) => {
    const productCountElement = querySelector('.js-product-count');
    productCountElement.textContent = totalProducts;
  };

  const attachProductListeners = () => {
    const products = document.querySelectorAll('.js-product');

    products.forEach((product) => {
      product.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = product.getAttribute('data-product-id');
        const url = product.getAttribute('data-url');

        // eslint-disable-next-line no-undef
        nostojs((api) => {
          api.recordSearchClick('category', { productId, url });
        });

        window.location.href = url;
      });
    });
  };

  const updateUrlWithFacets = (selectedFacets, currentPage, isPagination = false) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    // Add facets to URL
    Object.entries(selectedFacets).forEach(([facet, values]) => {
      params.delete(facet); // Remove existing facet values
      values.forEach((value) => {
        params.append(facet, value);
      });
    });

    // Preserve or set the current page parameter
    if (currentPage !== undefined) {
      params.set('page', currentPage + 1);
    } else {
      params.delete('page'); // Remove the page parameter if not paginating
    }

    // Sort parameters alphabetically
    const sortedParams = new URLSearchParams(
      [...params.entries()].sort(([a], [b]) => a.localeCompare(b)),
    );

    // Update the URL without reloading the page
    const newUrl = sortedParams.toString()
      ? `${url.pathname}?${sortedParams.toString()}`
      : url.pathname;

    // Use pushState for pagination to add history entries; otherwise, replaceState
    if (isPagination) {
      window.history.pushState(null, '', newUrl);
    } else {
      window.history.replaceState(null, '', newUrl);
    }
  };

  const updateUI = (
    { hits: products, facets, total },
    selectedFacets,
    currentPage,
    isPagination = false,
  ) => {
    console.log(currentPage);
    updateUrlWithFacets(selectedFacets, currentPage, isPagination);
    querySelector('.js-collection-products').innerHTML = generateProductsMarkup(products);
    attachProductListeners();
    querySelector('.js-collection-facets').innerHTML = generateFacetsMarkup(facets, selectedFacets);

    const productTypeFacet = facets.find((facet) => facet.field === 'customFields.producttype');
    if (productTypeFacet?.data) {
      const productTypes = productTypeFacet.data;
      initializeProductTypes(productTypes);
    }

    generatePagination(total, currentPage);
    populateProductCount(total);
  };

  const handleProductTypeChange = async (filter, collectionId, nostoId, sort = []) => {
    const result = await fetchProducts(collectionId, filter, sort, 0, nostoId);
    updateUI(result, filter, 0);
  };

  const getSelectedFacets = () => {
    const selectedFacets = {};

    // Get all checked checkboxes within the facets container
    document
      .querySelectorAll(".js-collection-facets input[type='checkbox']:checked")
      .forEach((checkbox) => {
        const [facetName, value] = checkbox.id.split('||');

        // If the facetName doesn't exist in the selectedFacets object, initialize it as an array
        if (!selectedFacets[facetName]) {
          selectedFacets[facetName] = [];
        }

        // Add the selected value to the corresponding facetName array
        selectedFacets[facetName].push(value ?? 'true');
      });

    document
      .querySelectorAll(
        '.js-product-types .product-type-facets--active:not(.product-type-facets--all)',
      )
      .forEach((productType) => {
        const facetName = 'customFields.producttype';
        const value = productType.textContent.trim();

        // If the facetName doesn't exist in the selectedFacets object, initialize it as an array
        if (!selectedFacets[facetName]) {
          selectedFacets[facetName] = [];
        }

        // Add the selected product type to the corresponding facetName array
        selectedFacets[facetName].push(value);
      });

    return selectedFacets;
  };

  const getSelectedFacetsFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const selectedFacets = {};

    // Convert params to an array of entries and use forEach
    Array.from(params.entries()).forEach(([key, value]) => {
      // Initialize array if the key doesn't exist
      if (!selectedFacets[key]) {
        selectedFacets[key] = [];
      }
      // Push the value into the array
      selectedFacets[key].push(value);
    });

    return selectedFacets;
  };

  const attachProductTypeListeners = (collectionId, nostoId) => {
    const productTypesContainer = document.querySelector('.js-product-types');
    const updateProductTypes = () => {
      const selectedFacets = getSelectedFacets();
      handleProductTypeChange(selectedFacets, collectionId, nostoId);
    };

    const handleAllProductsClick = () => {
      // Deactivate all other product types when "Alle Produkte" is clicked
      Array.from(productTypesContainer.querySelectorAll('.product-type-facets--active')).forEach(
        (elem) => {
          elem.classList.remove('product-type-facets--active');
        },
      );

      handleProductTypeChange({}, collectionId, nostoId);
    };

    productTypesContainer.addEventListener('click', (e) => {
      const allProductsTypeElem = productTypesContainer.querySelector('.product-type-facets--all');
      if (e.target.classList.contains('product-type-facets')) {
        // Handle "Alle Produkte" click
        if (e.target === allProductsTypeElem) {
          handleAllProductsClick();
        } else {
          // Toggle the clicked product type
          e.target.classList.toggle('product-type-facets--active');

          // Ensure "Alle Produkte" can't be deactivated if no other types are selected
          const activeTypes = productTypesContainer.querySelectorAll(
            '.product-type-facets--active:not(.product-type-facets--all)',
          );
          if (activeTypes.length === 0) {
            handleAllProductsClick();
          } else {
            updateProductTypes();
            const newAlleProdukteElem = productTypesContainer.querySelector(
              '.product-type-facets--all',
            );
            newAlleProdukteElem.classList.remove('product-type-facets--active');
          }
        }
      }
    });
  };

  const handleFacetChange = async (selectedFacets, collectionId, nostoId, sort) => {
    const result = await fetchProducts(collectionId, selectedFacets, sort, 0, nostoId);
    updateUI(result, selectedFacets, 0);
  };

  // Utility function to toggle dropdown visibility
  const toggleDropdown = (dropdown, isOpen) => {
    dropdown.classList.toggle('visible', isOpen);
    dropdown.previousElementSibling.setAttribute('aria-expanded', isOpen);
  };

  // Function to close any currently open dropdown
  const closeOpenDropdown = (openDropdown) => {
    if (openDropdown) {
      toggleDropdown(openDropdown, false);
      return null;
    }
    return openDropdown;
  };

  // Function to handle toggle button click
  const handleToggleClick = (clickedToggle, openDropdown) => {
    const dropdown = clickedToggle.nextElementSibling;

    // Close the currently open dropdown if it's not the same as the clicked one
    if (openDropdown && openDropdown !== dropdown) {
      closeOpenDropdown(openDropdown);
    }

    // Toggle the clicked dropdown
    const isOpen = !dropdown.classList.contains('visible');
    toggleDropdown(dropdown, isOpen);

    return isOpen ? dropdown : null;
  };

  // Function to handle checkbox change
  const handleCheckboxChange = (collectionId, nostoId, sort) => {
    const selectedFacets = getSelectedFacets();

    // Pass the dynamically gathered selectedFacets
    handleFacetChange(selectedFacets, collectionId, nostoId, sort);
  };

  // Main function to attach event listeners to the facets container
  const attachFacetListeners = (collectionId, nostoId, sort) => {
    const facetsContainer = document.querySelector('.js-collection-facets');
    let openDropdown = null;

    facetsContainer.addEventListener('click', (e) => {
      const clickedToggle = e.target.closest('.multi-select-toggle');
      const checkbox = e.target.closest("input[type='checkbox']");

      if (clickedToggle) {
        openDropdown = handleToggleClick(clickedToggle, openDropdown);
      }

      if (checkbox) {
        handleCheckboxChange(collectionId, nostoId, sort);
      }

      e.stopPropagation();
    });

    document.addEventListener('click', (e) => {
      if (!facetsContainer.contains(e.target)) {
        openDropdown = closeOpenDropdown(openDropdown);
      }
    });
  };

  const attachPaginationListeners = (selectedFacets, collectionId, nostoId, sort) => {
    paginationContainer.addEventListener('click', async (e) => {
      if (e.target.classList.contains('js-page-button')) {
        const newPage = Number(e.target.id);
        const result = await fetchProducts(collectionId, selectedFacets, sort, newPage, nostoId);
        updateUI(result, selectedFacets, newPage, true);
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }, 128);
      }
    });

    paginationContainer.addEventListener('submit', async (event) => {
      if (event.target.classList.contains('js-page-input')) {
        event.preventDefault();
        const input = event.target.querySelector('input[type="number"]');
        if (
          input.value &&
          Number(input.value) >= Number(input.min) &&
          Number(input.value) <= Number(input.max)
        ) {
          const newPage = Number(input.value) - 1;
          const result = await fetchProducts(collectionId, selectedFacets, sort, newPage, nostoId);
          updateUI(result, selectedFacets, newPage, true);

          setTimeout(() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }, 128);
        }
      }
    });
  };

  const initializeBackToTop = () => {
    const backToTopBtn = querySelector('.js-back-to-top');

    backToTopBtn.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  };

  const initializeDesktopScrollup = () => {
    const backToTopBtn = querySelector('.js-desktop-scrollup');

    backToTopBtn.addEventListener('click', () => {
      window.scrollTo({
        top: window.screenY + 320,
        behavior: 'smooth',
      });
    });
  };

  const initializeResetAll = (collectionId, nostoId, sort) => {
    const resetAllButton = querySelector('.js-reset-all');

    resetAllButton.addEventListener('click', () => {
      const checkboxes = document.querySelectorAll("input[type='checkbox']");
      const activeProductTypes = document.querySelectorAll('.product-type-facets--active');

      checkboxes.forEach((checkbox) => {
        // eslint-disable-next-line no-param-reassign
        checkbox.checked = false;
      });

      activeProductTypes.forEach((productType) => {
        productType.classList.remove('product-type-facets--active');
      });

      const selectedFacets = getSelectedFacets();
      handleFacetChange(selectedFacets, collectionId, nostoId, sort);
    });
  };

  const attachSortListeners = (collectionId, nostoId) => {
    const sortMenu = document.querySelector('.js-sort-menu');

    // Attach click event listeners to all sort links
    sortMenu.addEventListener('click', (event) => {
      const clickedLink = event.target.closest('.sort-dropdown__link');

      // Ensure the click was on a sort link
      if (clickedLink) {
        // Remove the active class from any currently active link
        const activeLink = sortMenu.querySelector('.sort-dropdown__link.active');
        if (activeLink) {
          activeLink.classList.remove('active');
        }

        // Add the active class to the clicked link
        clickedLink.classList.add('active');

        const field = clickedLink.getAttribute('data-field');
        const order = clickedLink.getAttribute('data-order');

        const filters = getSelectedFacets();
        handleProductTypeChange(filters, collectionId, nostoId, [{ field, order }]);
      }
    });
  };

  const handlePopState = async () => {
    const { collectionJson, nostoId } = getCollectionData();
    const selectedFacets = getSelectedFacetsFromUrl();
    const page = parseInt(new URLSearchParams(window.location.search).get('page')) || 1;
    const result = await fetchProducts(collectionJson.id, selectedFacets, [], page - 1, nostoId);
    updateUI(result, selectedFacets, page - 1);
  };

  const initialize = async () => {
    const { collectionJson, nostoId } = getCollectionData();
    const selectedFacets = getSelectedFacetsFromUrl();
    const initialData = await fetchProducts(collectionJson.id, selectedFacets, [], 0, nostoId);
    updateUI(initialData, selectedFacets, 0);
    attachFacetListeners(collectionJson.id, nostoId, []);
    attachPaginationListeners({}, collectionJson.id, nostoId, []);
    attachProductTypeListeners(collectionJson.id, nostoId);
    initializeBackToTop();
    initializeDesktopScrollup();
    initializeResetAll(collectionJson.id, nostoId, []);
    attachSortListeners(collectionJson.id, nostoId);

    window.addEventListener('popstate', handlePopState);
  };

  initialize();
})();
